import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { ChartLegendButton } from 'apps/sigma2-ui/src/pages/ProgressPage/ChartLegendButton';
import { S2GroupedBarChart } from 'apps/sigma2-ui/src/components/S2GroupedBarChart';
import { S2GroupedBarChartGroup } from 'apps/sigma2-ui/src/components/S2GroupedBarChart';
import { S2GroupedBarChartLevel } from 'libs/end-to-end/src/grouped-bar';
import { ChartTooltip } from 'apps/sigma2-ui/src/pages/ProgressPage/ChartTooltip';
import { getTooltipMetrics } from 'libs/end-to-end/src/progress-page-utils';
import { LevelValue } from 'libs/end-to-end/src/progress';
import { ProgressBarChartSerializableProps } from 'libs/end-to-end/src/progress';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { BarTotalPopoverLayer } from 'apps/sigma2-ui/src/components/BarTotalPopoverLayer';

export interface ProgressBarChartProps
  extends ProgressBarChartSerializableProps {
  getLinkToDeepDive: (
    treatmentDimensionLevel: string,
    indexValue: string,
  ) => Promise<string | undefined>;
  onLoad?: (selectedLevels: string[]) => void;
  overridingTooltip?: (groupId: string, value: LevelValue) => React.ReactNode;
  /**
   * If true, the chart will use horizontal scroll to always display in horizontal mode,
   * otherwise it will fallback to vertical mode when there is not enough horizontal space.
   * @default false
   */
  displayHorizontally?: boolean;
}

export function ProgressBarChart({
  data,
  metricDisplayName,
  units,
  getLinkToDeepDive,
  comparisonGroupValue,
  onLoad,
  isPrintMode,
  overridingTooltip,
  whiteLabelingSubjectPlural,
  viewConfiguration,
  groupLabels,
  isDisparities = false,
  displayHorizontally = false,
}: ProgressBarChartProps) {
  // memoized levels that will be used in the chart
  const initialChartLevels = useMemo(() => {
    const dedupedLevels: Record<string, LevelValue> = {};
    if (data.length > 0) {
      data[0].values.forEach((value) => {
        if (!dedupedLevels[value.level]) {
          dedupedLevels[value.level] = value;
        }
      });
    }
    return Object.values(dedupedLevels);
  }, [data]);

  // store selected levels. When list is empty all levels are displayed.
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);

  // callback to toggle a selected level
  const handleOnClickLevel = useCallback(
    (toggledLevel: string) => {
      setSelectedLevels((levels) => {
        if (levels.includes(toggledLevel)) {
          return levels.filter((d) => d !== toggledLevel);
        } else {
          return initialChartLevels
            .map(({ level }) => level)
            .filter((l) => levels.includes(l) || l === toggledLevel);
        }
      });
    },
    [initialChartLevels],
  );

  useEffect(() => {
    onLoad?.(selectedLevels);
  }, [onLoad, selectedLevels]);

  // callback to clear selected levels (select all)
  const handleOnClickAll = useCallback(() => {
    setSelectedLevels([]);
  }, []);

  const computedSelectedLevels = useMemo(() => {
    if (selectedLevels.length === 0) {
      return initialChartLevels.map(({ level }) => level);
    }
    return selectedLevels;
  }, [selectedLevels, initialChartLevels]);

  const language = useLanguage();

  const barChartUnits = useMemo(() => {
    if (viewConfiguration.overallDisplayMode === 'count') {
      return 'count';
    }

    return units;
  }, [units, viewConfiguration.overallDisplayMode]);

  const xGroups: S2GroupedBarChartGroup[] = useMemo(
    () =>
      data.map((year, yearIndex) => {
        const idAndLabel = groupLabels?.[year.year] || year.year;

        return {
          id: idAndLabel,
          label: idAndLabel,
          groupLevels: year.values
            .filter((value) => computedSelectedLevels.includes(value.level))
            .map((value) => {
              const tooltipMetrics = getTooltipMetrics({
                data,
                level: String(value.level),
                units,
                index: yearIndex,
                language,
                whiteLabelingSubjectPlural,
              });

              // The metrics which are percentages are not yet multiplied by 100, but the assumption on the bar chart is that they are.
              let valueAdjusted = 0;
              let valueOrZero = 0;

              if (viewConfiguration.overallDisplayMode === 'count') {
                valueOrZero = value.numerator ?? 0;
              } else {
                valueOrZero = value.value ?? 0;
              }

              // We only multiply by 100 if the units are percentage and we are not in disparities mode, thanks to a legacy decision to return those values pre-multiplied.
              if (isDisparities || barChartUnits !== 'percentage') {
                valueAdjusted = valueOrZero;
              } else {
                valueAdjusted = valueOrZero * 100;
              }

              return {
                id: value.isCheckered
                  ? `${value.level}_Checkered`
                  : value.level,
                value: valueAdjusted,
                isCheckered: value.isCheckered,
                tooltip: overridingTooltip ? (
                  overridingTooltip(year.year, value)
                ) : (
                  <ChartTooltip
                    color={value.color}
                    metrics={tooltipMetrics}
                    getLinkToDeepDive={(treatmentDimensionLevel) =>
                      getLinkToDeepDive(treatmentDimensionLevel, year.year)
                    }
                    level={value.level}
                    comparisonGroupValue={comparisonGroupValue}
                    whiteLabelingSubjectPlural={whiteLabelingSubjectPlural}
                  />
                ),
              };
            }),
        };
      }),
    [
      barChartUnits,
      comparisonGroupValue,
      computedSelectedLevels,
      data,
      getLinkToDeepDive,
      language,
      overridingTooltip,
      units,
      viewConfiguration,
      whiteLabelingSubjectPlural,
      isDisparities,
      groupLabels,
    ],
  );

  const xLevels: S2GroupedBarChartLevel[] = useMemo(
    () =>
      initialChartLevels
        .filter((lvl) => computedSelectedLevels.includes(lvl.level))
        .map((level) => {
          return {
            id: level.level,
            label: level.level,
            color: level.color,
          };
        }),
    [computedSelectedLevels, initialChartLevels],
  );

  const getExtraMarginRight = React.useCallback((maxXLength: number) => {
    return 5 * (maxXLength / 2);
  }, []);

  const getExtraMarginBottom = React.useCallback(
    (xAxisLabel: string | null | undefined) => {
      return xAxisLabel ? 30 : 0;
    },
    [],
  );

  const getExtraMarginLeft = React.useCallback((maxYLength: number) => {
    return 10 * (maxYLength / 2);
  }, []);

  return (
    <Spacing size={'2xl'} direction={'vertical'} className="w-full">
      <S2GroupedBarChart
        xGroups={xGroups}
        xLevels={xLevels}
        units={barChartUnits}
        yAxisLabel={metricDisplayName}
        xAxisLabel={language.progressChartXAxisLabel()}
        horizontalFixedHeight={true}
        horizontalHeight={332}
        horizontalMinBarWidth={15}
        verticalBarMinThickness={15}
        verticalBarMaxThickness={60}
        verticalInnerPadding={3}
        verticalGroupPaddingAsFactorOfHeight={0.3}
        displayHorizontally={displayHorizontally}
        getExtraMarginRight={getExtraMarginRight}
        getExtraMarginBottom={getExtraMarginBottom}
        getExtraMarginLeft={getExtraMarginLeft}
        layers={[BarTotalPopoverLayer]}
      />
      <Box
        display="flex"
        flexWrap="wrap"
        flexDirection="row"
        gap="xs"
        justifyContent="center"
      >
        {!isPrintMode && (
          <ChartLegendButton
            label="All"
            id="All"
            focused={selectedLevels.length === 0}
            onClick={handleOnClickAll}
            hasTransparentBorder={false}
          />
        )}
        {initialChartLevels.map((level) => (
          <ChartLegendButton
            key={level.level}
            id={level.level}
            label={level.level}
            iconColor={ConfigColors[level.color].Primary.bg}
            focused={selectedLevels.includes(level.level)}
            onClick={handleOnClickLevel}
            hasTransparentBorder={isPrintMode}
          />
        ))}
      </Box>
    </Spacing>
  );
}
